import React, { useState, useMemo, useRef } from 'react';
import { encode } from 'qss';
import { AnimatePresence, motion, useMotionValue, useSpring } from 'framer-motion';

const HoverPeek = ({
    children,
    url,
    peekWidth = 220,
    peekHeight = 140,
    isStatic = false,
    imageSrc = '',
    enableLensEffect = true,
    lensZoomFactor = 1.75,
    lensSize = 100,
}) => {
    const [open, setOpen] = useState(false);
    const [imgError, setImgError] = useState(false);
    const [isHoveringCard, setIsHoveringCard] = useState(false);
    const [lensPos, setLensPos] = useState({ x: 0, y: 0 });

    const mouseX = useMotionValue(0);
    const followX = useSpring(mouseX, { stiffness: 120, damping: 20 });

    const src = useMemo(() => {
        if (isStatic) return imageSrc;
        const params = encode({
            url,
            screenshot: true,
            meta: false,
            embed: 'screenshot.url',
            colorScheme: 'dark',
            'viewport.isMobile': true,
            'viewport.deviceScaleFactor': 1,
            'viewport.width': peekWidth * 2.5,
            'viewport.height': peekHeight * 2.5,
        });
        return `https://api.microlink.io/?${params}`;
    }, [isStatic, imageSrc, url, peekWidth, peekHeight]);

    const handlePointerMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const offsetFromCenter = (e.clientX - rect.left - rect.width / 2) * 0.3;
        mouseX.set(offsetFromCenter);
    };

    const handleLensMouseMove = (e) => {
        if (!enableLensEffect) return;
        const rect = e.currentTarget.getBoundingClientRect();
        setLensPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    };

    const maskValue = `radial-gradient(circle ${lensSize / 2}px at ${lensPos.x}px ${lensPos.y}px, black ${lensSize / 2}px, transparent ${lensSize / 2}px)`;

    const cardVariants = {
        initial: { opacity: 0, y: 10, rotateY: -25 },
        animate: { opacity: 1, y: 0, rotateY: 0, transition: { type: 'spring', stiffness: 200, damping: 18 } },
        exit: { opacity: 0, y: 6, transition: { duration: 0.15 } },
    };

    const lensVariants = {
        initial: { opacity: 0, scale: 0.7 },
        animate: { opacity: 1, scale: 1, transition: { duration: 0.2, ease: 'easeOut' } },
        exit: { opacity: 0, scale: 0.7, transition: { duration: 0.15 } },
    };

    return (
        <div
            style={{ position: 'relative', display: 'inline-block' }}
            onMouseEnter={() => { setOpen(true); mouseX.set(0); }}
            onMouseLeave={() => { setOpen(false); mouseX.set(0); setIsHoveringCard(false); }}
            onPointerMove={handlePointerMove}
        >
            {children}
            <AnimatePresence>
                {open && (
                    <div style={{ position: 'absolute', bottom: '100%', left: '50%', marginBottom: 10, zIndex: 9999, perspective: '800px' }}>
                        <motion.div
                            variants={cardVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            style={{ x: followX, translateX: '-50%' }}
                        >
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    position: 'relative', display: 'block', overflow: 'hidden',
                                    borderRadius: 8, backgroundColor: '#1a1a1a',
                                    border: '1px solid #404040',
                                    boxShadow: '0 12px 32px rgba(0,0,0,0.7)',
                                    padding: 2, textDecoration: 'none',
                                }}
                                onMouseEnter={() => enableLensEffect && setIsHoveringCard(true)}
                                onMouseLeave={() => enableLensEffect && setIsHoveringCard(false)}
                                onMouseMove={handleLensMouseMove}
                            >
                                {imgError ? (
                                    <div style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        backgroundColor: '#262626', color: '#737373',
                                        fontSize: 12, fontFamily: 'sans-serif',
                                        width: peekWidth, height: peekHeight, borderRadius: 5,
                                    }}>
                                        Preview unavailable
                                    </div>
                                ) : (
                                    <img
                                        src={src}
                                        width={peekWidth}
                                        height={peekHeight}
                                        style={{ display: 'block', borderRadius: 5, pointerEvents: 'none', backgroundColor: '#262626', verticalAlign: 'top' }}
                                        alt={`Preview for ${url}`}
                                        onError={() => setImgError(true)}
                                        loading="lazy"
                                    />
                                )}
                                <AnimatePresence>
                                    {enableLensEffect && isHoveringCard && !imgError && (
                                        <motion.div
                                            variants={lensVariants}
                                            initial="initial"
                                            animate="animate"
                                            exit="exit"
                                            style={{
                                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                                pointerEvents: 'none', overflow: 'hidden', borderRadius: 8,
                                                maskImage: maskValue,
                                                WebkitMaskImage: maskValue,
                                            }}
                                        >
                                            <div style={{
                                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                                transform: `scale(${lensZoomFactor})`,
                                                transformOrigin: `${lensPos.x}px ${lensPos.y}px`,
                                            }}>
                                                <img
                                                    src={src}
                                                    width={peekWidth}
                                                    height={peekHeight}
                                                    style={{ display: 'block', borderRadius: 5, backgroundColor: '#262626', verticalAlign: 'top' }}
                                                    alt=""
                                                    aria-hidden="true"
                                                />
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </a>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default HoverPeek;
